import { useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';

const InstagramSlides = ({ content, title, loading }) => {
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);
    const [swiper, setSwiper] = useState(null);

    useEffect(() => {
        if (!swiper || !prevButtonRef.current || !nextButtonRef.current) return;

        swiper.params.navigation.prevEl = prevButtonRef.current;
        swiper.params.navigation.nextEl = nextButtonRef.current;
        swiper.params.navigation.disabledClass = 'opacity-30 hover:opacity-30';

        swiper.navigation.init();
        swiper.navigation.update();

        swiper.on('slideChange', () => {
            setTimeout(() => {
                swiper.navigation.update();
            }, 10);
        });
    }, [swiper, prevButtonRef.current, nextButtonRef.current]);

    return (
        <div className="my-10">
            <h3 className="text-2xl xl:text-4xl text-secondary font-bold mb-4 xl:mb-8">{title}</h3>

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-10 opacity-20">
                    {[...Array(4)].map((_, index) => (
                        <div
                            key={index}
                            role="status"
                            className="flex items-center justify-center h-0 pb-[90%] md:pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="relative">
                    <Swiper
                        spaceBetween={20}
                        slidesPerView={3.4}
                        modules={[Navigation]}
                        onSwiper={(swiperInstance) => {
                            setSwiper(swiperInstance);
                        }}
                        breakpoints={{
                            1024: {
                                slidesPerView: 4.4,
                                spaceBetween: 20,
                            },
                            900: {
                                slidesPerView: 3.8,
                                spaceBetween: 15,
                            },
                            768: {
                                slidesPerView: 3.2,
                            },
                            0: {
                                slidesPerView: 2.3,
                                spaceBetween: 12,
                            },
                        }}
                    >
                        {content.map((post, index) => (
                            <SwiperSlide key={index}>
                                <a href={`${post.url}`} className="group block relative" target="_blank" rel="noreferrer">
                                    <div className="relative h-0 pb-[100%] rounded-xl overflow-hidden border border-neutral-500">
                                        <img
                                            src={post.local_url}
                                            alt={post.caption}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            loading="lazy"
                                        />

                                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent lg:opacity-0 transition-all duration-300 group-hover:scale-y-125 group-hover:opacity-100" />

                                        <h3 className="absolute left-4 lg:left-6 right-4 lg:right-6 top-1/2 max-sm:text-xs max-sm:tracking-tight lg:text-lg text-white text-center font-light lg:opacity-0 max-md:-translate-y-1/2 line-clamp-3 transition-all duration-300 group-hover:-translate-y-1/2 group-hover:opacity-100">
                                            {post.caption}
                                        </h3>
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <button
                        ref={prevButtonRef}
                        className="absolute -left-4 top-1/2 -translate-y-1/2 z-[1] w-8 h-8 bg-white bg-opacity-40 opacity-70 rounded-full cursor-pointer transition-all ease-out duration-200 hover:opacity-80 before:content-[''] before:absolute before:top-2.5 before:left-3 before:w-3 before:h-3 before:border-t-2 before:border-l-2 before:-rotate-45"
                    ></button>

                    <button
                        ref={nextButtonRef}
                        className="absolute -right-4 top-1/2 -translate-y-1/2 z-[1] w-8 h-8 bg-white bg-opacity-40 opacity-70 rounded-full cursor-pointer transition-all ease-out duration-200 hover:opacity-80 before:content-[''] before:absolute before:top-2.5 before:right-3 before:w-3 before:h-3 before:border-b-2 before:border-r-2 before:-rotate-45"
                    ></button>
                </div>
            )}
        </div>
    );
};

export default InstagramSlides;
